import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, ImageIcon } from "lucide-react";
import { assetUrl } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Lightbox } from "@/components/site/Lightbox";

const usePerView = () => {
  const get = () => (window.innerWidth >= 1024 ? 5 : window.innerWidth >= 640 ? 3 : 2);
  const [pv, setPv] = useState(get);
  useEffect(() => {
    const on = () => setPv(get());
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  return pv;
};

const Placeholders = ({ id }) => (
  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 px-4 sm:px-14" data-testid={`${id}-placeholders`}>
    {[0, 1, 2, 3, 4].map((i) => (
      <div key={i} className={cn("aspect-[4/5] rounded-xl border-2 border-dashed border-brass/50 flex flex-col items-center justify-center gap-2 text-sm text-charcoal/50", i > 1 && "hidden sm:flex", i > 2 && "sm:hidden lg:flex")}>
        <ImageIcon className="h-6 w-6" />
        Image coming soon
      </div>
    ))}
  </div>
);

export const ImageCarousel = ({ images = [], id }) => {
  const perView = usePerView();
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [open, setOpen] = useState(null);
  const steps = Math.max(1, images.length - perView + 1);

  useEffect(() => {
    if (idx >= steps) setIdx(0);
  }, [steps, idx]);

  useEffect(() => {
    if (paused || steps <= 1) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % steps), 3500);
    return () => clearInterval(t);
  }, [paused, steps]);

  if (!images.length) return <Placeholders id={id} />;
  const go = (d) => setIdx((i) => (i + d + steps) % steps);
  const arrow = "absolute top-1/2 -translate-y-1/2 h-9 w-9 sm:h-10 sm:w-10 rounded-full bg-white shadow-md flex items-center justify-center text-brand hover:bg-brand hover:text-white transition-colors duration-200";

  return (
    <div className="relative px-12 sm:px-14" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} data-testid={`${id}-carousel`}>
      <div className="overflow-hidden">
        <div className="flex items-center transition-transform duration-700 ease-[cubic-bezier(.22,1,.36,1)]" style={{ transform: `translateX(-${(idx * 100) / perView}%)` }}>
          {images.map((src, i) => (
            <button key={`${src}-${i}`} type="button" onClick={() => setOpen(assetUrl(src))} className="shrink-0 px-2 group" style={{ width: `${100 / perView}%` }} data-testid={`${id}-item-${i}`}>
              <img src={assetUrl(src)} alt={`Slide ${i + 1}`} loading="lazy" className="block w-full h-auto rounded-lg shadow-sm transition-transform duration-300 group-hover:scale-[1.02]" />
            </button>
          ))}
        </div>
      </div>
      {steps > 1 && (
        <>
          <button type="button" aria-label="Previous" onClick={() => go(-1)} className={cn(arrow, "left-0 sm:left-1")} data-testid={`${id}-prev`}><ChevronLeft className="h-5 w-5" /></button>
          <button type="button" aria-label="Next" onClick={() => go(1)} className={cn(arrow, "right-0 sm:right-1")} data-testid={`${id}-next`}><ChevronRight className="h-5 w-5" /></button>
          <div className="flex justify-center gap-2 mt-6">
            {Array.from({ length: steps }).map((_, i) => (
              <button key={i} type="button" aria-label={`Go to ${i + 1}`} onClick={() => setIdx(i)} className={cn("h-2 rounded-full transition-all duration-300", i === idx ? "w-6 bg-brand" : "w-2 bg-charcoal/25")} data-testid={`${id}-dot-${i}`} />
            ))}
          </div>
        </>
      )}
      <Lightbox src={open} onClose={() => setOpen(null)} />
    </div>
  );
};
